import {Grid, Box, Text, useToast, Flex, Button, useDisclosure, IconButton, Tooltip, Badge, Icon, Menu, MenuButton, MenuList, MenuItem, MenuDivider} from "@chakra-ui/react";
import axios from "axios";
import {crud, url} from "../../crudRequests";
import {createBookingWizardUrl} from "../../lib/bookingWizard";

import Appointment from "./Appointment";
import CalendarSync from "./CalendarSync";
import {useState, useEffect, useMemo, useCallback} from "react";

import {fetchBilling} from "./helpers/fetchBilling";
import PreviewFile from "../Files/PreviewFile";
import PreviewInvRecFile from "../Files/InvRec";
import AppointmentNotes from "./AppointmentNotes";
import WaitlistRequests from "../../features/waitlist/WaitlistRequests";
import useMyWaitlistRequests from "../../features/waitlist/useMyWaitlistRequests";
import {FiPlus, FiRefreshCw, FiCalendar, FiMapPin, FiChevronDown} from "react-icons/fi";
import {LuCalendarRange} from "react-icons/lu";
import {allAsSeenBy, participationQuery} from "../../services/schedule/ownParticipation";

export default function Appointments({dashState, dispatch}) {
  let patient = dashState.patient;
  const [billing, setBilling] = useState([]);
  const [preview, setPreview] = useState(false);
  const [previewInvRec, setPreviewInvRec] = useState(false);
  const [selectedTr, setSelectedTr] = useState(false);
  const [apptNotes, setApptNotes] = useState(null);
  const [activeFilter, setActiveFilter] = useState("upcoming");
  // Loaded here rather than inside the panel, because the TAB needs the count.
  // Two components fetching the same list is two answers to "how many am I
  // waiting on" on one screen.
  const waitlist = useMyWaitlistRequests(dashState);
  const toast = useToast();
  const [refreshing, setRefreshing] = useState(false);

  const {isOpen: isCalendarSyncOpen, onOpen: onCalendarSyncOpen, onClose: onCalendarSyncClose} = useDisclosure();

  const refreshAppointments = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await crud(dashState, [
        {
          db: dashState.db,
          collection: "appointments",
          // Every visit naming them (P3, services/schedule/ownParticipation.js).
          parameters: [participationQuery(dashState.patient?.pid)],
          method: "find",
          findCursor: [{method: "sort", arguments: [{startAt: -1}]}],
        },
      ]);
      // Each visit as their own place in it says (§2.8 rule 4).
      dispatch({type: "SET_APPOINTMENTS", payload: allAsSeenBy(res.data?.[0] || [], dashState.patient?.pid)});
      fetchBilling(setBilling, dashState, patient, toast);
    } catch (error) {
      console.error("Failed to refresh appointments:", error);
      toast({
        title: "Refresh failed",
        description: "Could not refresh appointments. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setRefreshing(false);
    }
  }, [dashState, dispatch, patient, toast]);

  useEffect(() => {
    fetchBilling(setBilling, dashState, patient, toast);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doctorsMap = useMemo(() => {
    if (dashState.doctor) return {[dashState.doctor.did]: dashState.doctor};
    return (
      dashState.doctors?.reduce((ac, e) => {
        ac[e.did] = e;
        return ac;
      }, {}) || {}
    );
  }, []);

  const billingMap = useMemo(() => {
    return (
      billing?.reduce((ac, e) => {
        ac[e.aid] = e;
        return ac;
      }, {}) || {}
    );
  }, [billing]);

  const now = Date.now();
  const allAppointments = dashState?.appointments || [];

  const upcoming = allAppointments.filter(
    (a) => !a.cancelled && a.startAt > now
  );
  const past = allAppointments.filter(
    (a) => !a.cancelled && a.startAt <= now
  );
  const cancelled = allAppointments.filter((a) => a.cancelled);

  const filtered =
    activeFilter === "upcoming"
      ? upcoming
      : activeFilter === "past"
      ? past
      : activeFilter === "cancelled"
      ? cancelled
      : allAppointments;

  return (
    <Box flex={1} overflow="hidden" display="flex" flexDirection="column">
      <AppointmentNotes {...{apptNotes, setApptNotes}} />
      <CalendarSync
        isOpen={isCalendarSyncOpen}
        onClose={onCalendarSyncClose}
        state={dashState}
        onSync={() => {
          toast({
            title: "Calendar Updated",
            description: "Your appointments are now synced to your calendar",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        }}
      />

      {previewInvRec && (
        <PreviewInvRecFile
          preview={previewInvRec}
          setPreview={setPreviewInvRec}
          appointment={selectedTr}
          state={dashState}
        />
      )}
      {preview && (
        <PreviewFile
          preview={preview}
          setPreview={setPreview}
          state={dashState}
        />
      )}

      {/* Toolbar: filters + actions */}
      <Flex
        align="center"
        justify="space-between"
        mb={4}
        flexWrap="wrap"
        gap={3}
        flexShrink={0}
      >
        {/* Filter tabs */}
        <Flex
          gap={1}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
          p="3px"
        >
          <FilterTab
            label="Upcoming"
            count={upcoming.length}
            isActive={activeFilter === "upcoming"}
            onClick={() => setActiveFilter("upcoming")}
            accentColor="#6772e5"
          />
          <FilterTab
            label="Past"
            count={past.length}
            isActive={activeFilter === "past"}
            onClick={() => setActiveFilter("past")}
          />
          <FilterTab
            label="Cancelled"
            count={cancelled.length}
            isActive={activeFilter === "cancelled"}
            onClick={() => setActiveFilter("cancelled")}
            accentColor="#e53e3e"
          />
          <FilterTab
            label="All"
            count={allAppointments.length}
            isActive={activeFilter === "all"}
            onClick={() => setActiveFilter("all")}
          />
          {/* Not a filter over appointments like the four beside it: a waitlist
              request has no date and is not a state an appointment can be in.
              It sits here anyway because a tab is where a patient looks for
              "my waitlist". Client ticket 328. */}
          <FilterTab
            label="Waitlist"
            count={waitlist.entries.length}
            isActive={activeFilter === "waitlist"}
            onClick={() => setActiveFilter("waitlist")}
          />
        </Flex>

        {/* Action buttons */}
        <Flex gap={2}>
          <Tooltip label="Refresh appointments" hasArrow>
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Refresh appointments"
              isLoading={refreshing}
              onClick={refreshAppointments}
              icon={<FiRefreshCw size={14} />}
              color="#6b7280"
              _hover={{color: "#1a1f36", bg: "#f8f9fa"}}
              borderRadius="6px"
            />
          </Tooltip>
          <Button
            size="sm"
            variant="ghost"
            color="#6b7280"
            fontWeight="500"
            fontSize="13px"
            leftIcon={<FiCalendar size={14} />}
            _hover={{color: "#1a1f36", bg: "#f8f9fa"}}
            borderRadius="6px"
            onClick={onCalendarSyncOpen}
          >
            Sync
          </Button>
          {(() => {
            // Org opt-out (Clinic > Features > "Hide Patient Self-Booking").
            // Guards both renderings below — the single button and the
            // multi-location menu. Reschedule/Cancel are unaffected: they live
            // on each appointment card and follow the cancellation policy.
            if (dashState?.organization?.features?.hidePatientSelfBooking) {
              return null;
            }
            const patientLids = Array.isArray(dashState.patient?.lid)
              ? dashState.patient.lid.filter(Boolean)
              : (dashState.patient?.lid ? [dashState.patient.lid] : []);
            const allLocs = dashState.locations || [];
            const accessibleLocs = patientLids.length > 0
              ? allLocs.filter((l) => patientLids.includes(l.lid))
              : allLocs;
            const showPicker = accessibleLocs.length > 1;

            const openWizard = (lid) => {
              // Booking for the signed-in patient. A guardian booking for a
              // dependent goes through the dependent's own screen, which
              // names them explicitly; this screen only ever books for its
              // own account holder. The site is signed in already: the
              // portal's session is its own (blhWebServer ADR 0022).
              window.open(createBookingWizardUrl(dashState, {locationId: lid}), "_blank");
            };

            if (!showPicker) {
              const lid = accessibleLocs[0]?.lid || null;
              return (
                <Button
                  size="sm"
                  bg="#6772e5"
                  color="white"
                  _hover={{bg: "#5b63d3"}}
                  fontWeight="500"
                  fontSize="13px"
                  leftIcon={<FiPlus size={14} />}
                  borderRadius="6px"
                  onClick={() => openWizard(lid)}
                >
                  Book
                </Button>
              );
            }

            return (
              <Menu placement="bottom-end">
                <MenuButton
                  as={Button}
                  size="sm"
                  bg="#6772e5"
                  color="white"
                  _hover={{bg: "#5b63d3"}}
                  _active={{bg: "#5b63d3"}}
                  fontWeight="500"
                  fontSize="13px"
                  leftIcon={<FiPlus size={14} />}
                  rightIcon={<FiChevronDown size={14} />}
                  borderRadius="6px"
                >
                  Book
                </MenuButton>
                <MenuList minW="280px" maxH="360px" overflowY="auto" zIndex={20}>
                  <MenuItem onClick={() => openWizard(null)} _hover={{bg: "gray.50"}}>
                    <Box>
                      <Text fontSize="14px" fontWeight="500" color="gray.900">All locations</Text>
                      <Text fontSize="12px" color="gray.500">Browse across your locations</Text>
                    </Box>
                  </MenuItem>
                  <MenuDivider />
                  {accessibleLocs.map((loc) => (
                    <MenuItem
                      key={loc.lid}
                      icon={<FiMapPin size={14} color="#6b7280" />}
                      onClick={() => openWizard(loc.lid)}
                      _hover={{bg: "gray.50"}}
                    >
                      <Box>
                        <Text fontSize="14px" fontWeight="500" color="gray.900">
                          {loc.name || loc.locationName || "Location"}
                        </Text>
                        {loc.address && (
                          <Text fontSize="12px" color="gray.500" noOfLines={1}>
                            {loc.address}
                          </Text>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            );
          })()}
        </Flex>
      </Flex>

      {/* Appointment grid */}
      <Box flex={1} overflowY="auto" mr={-1} pr={1}>
        {activeFilter === "waitlist" ? (
          <WaitlistRequests dashState={dashState} {...waitlist} />
        ) : filtered.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={16}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="8px"
          >
            <Icon as={LuCalendarRange} fontSize="32px" color="gray.300" mb={3} />
            <Text fontSize="14px" color="#6b7280" fontWeight="500">
              {activeFilter === "upcoming"
                ? "No upcoming appointments"
                : activeFilter === "past"
                ? "No past appointments"
                : activeFilter === "cancelled"
                ? "No cancelled appointments"
                : "No appointments yet"}
            </Text>
            <Text fontSize="12px" color="gray.400" mt={1}>
              {activeFilter === "upcoming"
                ? "Book an appointment to get started"
                : "Your appointments will appear here"}
            </Text>
          </Flex>
        ) : (
          <Grid
            templateColumns={{
              base: "1fr",
              lg: "1fr 1fr",
            }}
            gap={3}
          >
            {filtered.map((appointment) => (
              <Appointment
                apptDoctor={doctorsMap[appointment.did]}
                setApptNotes={setApptNotes}
                dispatch={dispatch}
                state={dashState}
                key={appointment.aid}
                appointment={appointment}
                transaction={billingMap[appointment.aid]}
                setPreview={setPreview}
                setPreviewInvRec={setPreviewInvRec}
                setSelectedTr={setSelectedTr}
              />
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
}

function FilterTab({label, count, isActive, onClick, accentColor}) {
  return (
    <Button
      size="xs"
      variant="ghost"
      onClick={onClick}
      bg={isActive ? "#f8f9fa" : "transparent"}
      color={isActive ? "#1a1f36" : "#6b7280"}
      fontWeight={isActive ? "600" : "500"}
      fontSize="12px"
      borderRadius="6px"
      px={3}
      py={1}
      h="28px"
      _hover={{bg: "#f8f9fa"}}
      transition="all 0.15s ease"
    >
      {label}
      {count > 0 && (
        <Badge
          ml={1.5}
          fontSize="10px"
          fontWeight="600"
          bg={
            isActive && accentColor
              ? accentColor
              : isActive
              ? "#1a1f36"
              : "gray.200"
          }
          color={isActive ? "white" : "#6b7280"}
          borderRadius="full"
          px={1.5}
          minW="18px"
          textAlign="center"
          lineHeight="18px"
        >
          {count}
        </Badge>
      )}
    </Button>
  );
}
